import { ApolloClient, useApolloClient } from '@provision/graphql-client';
import {
  useDefaultDeletesSubscription,
  useDefaultUpdatesSubscription,
  WebSocketEventBus,
} from '@provision/ui-real-time-events';
import { useEffect, useRef } from 'react';
import { ignoreImmediateRefetchOnWsReconnectQueries } from 'src/graphql/setup/constants';
import { queryRegistry } from 'src/graphql/setup/query-registry';
import { useNotifications } from 'src/utils/hooks';
import { useConversationMessageSubscription } from './use-conversation-message-subscription';
import { TriggerInvalidate, useInvalidateAndRefetch } from './use-invalidate-and-refetch';
import { useQueryEvictionSubscription } from './use-query-eviction-subscription';

const RECONNECTION_TIME_SECONDS = 5;

export const useGlobalEventHubSubscription = (wsEventBus: WebSocketEventBus) => {
  const client = useApolloClient();
  const { showSuccessAlert, showErrorAlert } = useNotifications();
  const invalidateQuery = useInvalidateAndRefetch();

  const disconnectAt = useRef<number>(0);

  useDefaultUpdatesSubscription(wsEventBus, client);
  useDefaultDeletesSubscription(wsEventBus, client);
  useQueryEvictionSubscription(wsEventBus, client);
  useConversationMessageSubscription(wsEventBus, client);

  useEffect(() => {
    // Initial connection.
    wsEventBus.connect();

    const subDisconnect = wsEventBus.onDisconnect().subscribe(() => {
      console.info('WS connection disconnected');
      if (!disconnectAt.current) {
        disconnectAt.current = Date.now();
      }
    });

    const subReconnect = wsEventBus.onReconnect().subscribe(() => {
      if (!disconnectAt.current) return;

      const downSeconds = (Date.now() - disconnectAt.current) / 1_000;

      console.info(`WS reconnection succeed, disconnection lasted ${downSeconds} seconds`);

      disconnectAt.current = 0;

      if (downSeconds < RECONNECTION_TIME_SECONDS) {
        return;
      }
      return refreshActiveQueries({ client, invalidateQuery });
    });

    function connect() {
      wsEventBus.connect();
      showSuccessAlert('You are back online');
    }

    function disconnect() {
      wsEventBus.disconnect();
      showErrorAlert('Application is offline. Changes will not be saved.', { autoHide: null });
    }

    window.addEventListener('online', connect);
    window.addEventListener('offline', disconnect);

    /* cleanup */
    return () => {
      subDisconnect.unsubscribe();
      subReconnect.unsubscribe();
      window.removeEventListener('online', connect);
      window.removeEventListener('offline', disconnect);
    };
  }, [wsEventBus, showSuccessAlert, showErrorAlert, client, invalidateQuery]);
};

async function refreshActiveQueries(params: {
  client: ApolloClient<unknown>;
  invalidateQuery: TriggerInvalidate;
  lightWeight?: boolean;
}) {
  const { invalidateQuery, lightWeight } = params;
  const activeQueries = lightWeight
    ? queryRegistry.getQueriesForEntity('ConversationMessage')
    : queryRegistry.getAllQueries();

  const matchedQueries = activeQueries.filter((oq) =>
    lightWeight
      ? activeQueries
      : !ignoreImmediateRefetchOnWsReconnectQueries.has(oq.operationName),
  );

  await Promise.all(
    matchedQueries.map((query) => {
      return invalidateQuery({
        operationName: query.operationName,
        variables: query.variables as Record<string, unknown>,
        query: query.query,
        hashKey: query.key,
      });
    }),
  );
}
